import { Card, CardContent } from '@ui/card.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import { unparse } from 'papaparse';
import { targetsImportBody } from '@r/api/generated-zod/index.ts';
import { useFileParserWithErrors } from '@r/hooks/use-file-parser-patch-with-errors.ts';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { useTranslation } from 'react-i18next';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

export default function TargetsImport() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  // Utiliser le schéma Zod généré par Orval pour les données d'import de targets
  // Le schéma targetsImportBody a la structure { data: [...] }
  // Chaque item du tableau correspond à une ligne CSV
  const targetsImportSchema = targetsImportBody.shape.data.element;
  const { onParse } = useFileParserWithErrors(targetsImportSchema, '/admin/targets/import');
  const { state, setErrorState, setPreviewState, resetState } = useImportState('targets');
  const { sanitizedWorkspaceName } = useWorkspaceName();

  const fields = Object.keys(targetsImportSchema.shape);

  const csv = unparse({
    fields,
    data: [
      ['12345', 'global', 5000],
      ['87454', 'stock', 10000]
    ]
  });

  const filename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_import_target_example.csv`
    : 'import_target_example.csv';

  const handleParse = async (files: File[]) => {
    const toastId = toast.loading(t('import.process.analyzing'));

    try {
      const result = await onParse(files);

      if (result.isPreview) {
        toast.dismiss(toastId);
        setPreviewState(result.errors ?? [], files[0], result.analysis ?? null);
      } else if (result.success) {
        toast.success(result.message || t('import.process.success'), { id: toastId });
        resetState();
      } else if (result.errors && result.errors.length > 0) {
        toast.dismiss(toastId);
        setErrorState(result.errors, files[0]);
      }
    } catch (error) {
      showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
        id: toastId
      });
    }
  };

  const handleRetry = async () => {
    if (state.originalFile) {
      const toastId = toast.loading(t('import.process.loading'));

      try {
        const result = await onParse([state.originalFile], true);

        if (result.success) {
          toast.success(result.message || t('import.process.success'), { id: toastId });
          resetState();
        } else {
          toast.error(result.message || t('import.process.error'), { id: toastId });
          if (result.errors && result.errors.length > 0) {
            setErrorState(result.errors, state.originalFile);
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
          id: toastId
        });
      }
    }
  };

  if (state.isPreview && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="targets"
        analysis={state.analysis}
      />
    );
  }

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('admin.companies.targets.import.title')}</h1>

      <Card>
        <CardContent className="pt-6">
          <CsvFile csv={csv} filename={filename} label={t('common.csv.example.button')} />

          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={handleParse}
          />
        </CardContent>
      </Card>
    </div>
  );
}
